/**
 * Per-kind job adapters for the global job-progress UX.
 *
 * Each adapter maps a job kind → its display label/accent, how to poll it
 * (`statusPath`, omitted for `upload` which is fed by useUploadJobsWatcher), and
 * a `normalize(raw, meta)` that turns the raw status payload into the single
 * shape the modal/toast render:
 *
 *   { statusKind, phaseLabel, currentStep, percent|null, counts, raw }
 *
 * The 3 admin tools subclass AsyncJobColumnsMixin (identical payload shape), so
 * they share `normalizeAsyncJob`. Bulk upload coalesces the design/etsy field
 * divergence (`total_files` vs `total_designs`).
 */
import API_ENDPOINTS from '../constants/apiEndpoints';
import { mapStatus, phaseLabel } from './jobStatus';

function pct(completed, total) {
  if (!total || total <= 0) return null;
  return Math.min(100, Math.round((completed / total) * 100));
}

function normalizeAsyncJob(kind, raw) {
  const total = raw?.total_designs ?? 0;
  const completed = raw?.completed ?? 0;
  const statusKind = mapStatus(raw?.status);
  const headline = statusKind === 'paused' ? 'Needs your input' : phaseLabel(kind, raw?.phase);
  // The worker writes a live step into `message` mid-run; fall back to counts.
  let currentStep = raw?.message || null;
  if (!currentStep && total > 0 && statusKind === 'active') {
    currentStep = `${completed} of ${total}`;
  }
  return {
    statusKind,
    phaseLabel: headline,
    currentStep,
    percent: statusKind === 'paused' ? null : pct(completed, total),
    counts: {
      completed,
      total,
      succeeded: raw?.succeeded_count ?? 0,
      failed: raw?.failed_count ?? 0,
    },
    raw,
  };
}

function normalizeUpload(raw, meta) {
  const phaseKind = meta?.phaseKind || 'design';
  const total = raw?.total_designs ?? raw?.total_files ?? 0; // field-name divergence
  const completed = raw?.completed ?? 0;
  return {
    statusKind: mapStatus(raw?.status),
    phaseLabel: phaseKind === 'etsy' ? 'Listing to Etsy' : 'Processing designs',
    currentStep:
      total > 0
        ? `${phaseKind === 'etsy' ? 'Listed to draft' : 'Processed'} ${completed} of ${total}`
        : raw?.message || null,
    percent: pct(completed, total),
    counts: {
      completed,
      total,
      succeeded: raw?.succeeded_count ?? 0,
      failed: raw?.failed_count ?? 0,
    },
    raw,
  };
}

export const ADAPTERS = {
  'shop-reset': {
    label: 'Shop reset',
    accent: 'amber',
    statusPath: id => API_ENDPOINTS.SHOP_RESET.STATUS(id),
    normalize: raw => normalizeAsyncJob('shop-reset', raw),
  },
  'mockup-regen': {
    label: 'Mockup regen',
    accent: 'sage',
    statusPath: id => API_ENDPOINTS.MOCKUP_REGEN.STATUS(id),
    normalize: raw => normalizeAsyncJob('mockup-regen', raw),
  },
  'listing-replace': {
    label: 'Listing replace',
    accent: 'rose',
    statusPath: id => API_ENDPOINTS.LISTING_REPLACE.STATUS(id),
    normalize: raw => normalizeAsyncJob('listing-replace', raw),
  },
  upload: {
    label: 'Bulk upload',
    accent: 'indigo',
    externallyFed: true, // fed by useUploadJobsWatcher; the provider does not poll it
    normalize: (raw, meta) => normalizeUpload(raw, meta),
  },
};

export function getAdapter(kind) {
  return ADAPTERS[kind] || ADAPTERS['listing-replace'];
}
